import { UseMutateAsyncFunction } from "@tanstack/react-query"
import { AriaAttributes, AriaRole, ReactNode, startTransition, useOptimistic, useRef } from "react"

export function GoButton({role, className, disabledBecause, href, children, target, ...rest}: {
	role?: AriaRole
	className?: string
	disabledBecause?: string
	href: string | null
	children: ReactNode
	target?: '_blank'
} & AriaAttributes) {
	return href ? <a role={role ?? 'button'} className={className} aria-disabled={disabledBecause !== undefined} title={disabledBecause} href={disabledBecause ? undefined : href} target={target} {...rest}>{children}</a> : <></>
}

interface MutationButtonAttrs<TData, TError> {
	role?: AriaRole
	className?: string
	disabledBecause?: string
	onSuccess?: (ev: MutationSuccessEvent<TData, HTMLButtonElement>) => void
	onError?: (ev: MutationErrorEvent<TError, HTMLButtonElement>) => void
	children: ReactNode
}

export function MutateButton<TData = unknown, TError = Error>({...rest}: {
	mutation: {
		mutateAsync: UseMutateAsyncFunction<TData, TError, void>
		isPending: boolean
	} | null | undefined
} & MutationButtonAttrs<TData, TError> & AriaAttributes) {
	return <_MutationButton type={undefined} vars={undefined} {...rest}/>
}

export function SubmitButton<TData = unknown, TError = Error, TVariables = void>({...rest}: {
	mutation: {
		mutateAsync: UseMutateAsyncFunction<TData, TError, TVariables>
		isPending: boolean
	} | null | undefined
	vars: (data: FormData) => TVariables
} & MutationButtonAttrs<TData, TError> & AriaAttributes) {
	return <_MutationButton type="submit" {...rest}/>
}

function _MutationButton<TData = unknown, TError = Error, TVariables = void>({disabledBecause, mutation, vars, onSuccess, onError, children, ...rest}: {
	type: 'submit' | undefined
	mutation: {
		mutateAsync: UseMutateAsyncFunction<TData, TError, TVariables>
		isPending: boolean
	} | null | undefined
	vars: undefined | ((data: FormData) => TVariables)
} & MutationButtonAttrs<TData, TError> & AriaAttributes) {
	const button = useRef<HTMLButtonElement>(null)
	const [isPending, setIsPending] = useOptimistic(false)

	async function mutate(variables: TVariables) {
		if (disabledBecause || !mutation) return
		setIsPending(true)
		try {
			await mutation.mutateAsync(variables, {
				onSuccess: onSuccess ? data => {
					onSuccess(new MutationSuccessEvent('success', {data}, button.current!))
				} : undefined,
				onError: onError ? error => {
					onError(new MutationErrorEvent('error', {error}, button.current!))
				} : undefined,
			})
		}
		catch (e) {} // handled by the mutation system
		finally {
			// TODO remove if they fix https://github.com/facebook/react/issues/33545
			startTransition(() => {
				setIsPending(false)
			})
		}
	}
	return mutation !== null ? <button
		ref={button}
		disabled={disabledBecause !== undefined || !mutation || mutation.isPending || isPending}
		data-pending={isPending ? true : undefined}
		title={disabledBecause ?? (!mutation ? 'Loading…' : undefined)}
		formAction={vars ? async data => {
			await mutate(vars(data))
		} : undefined}
		onClick={!vars ? ev => {
			if (ev.defaultPrevented) return
			ev.preventDefault()
			startTransition(async () => {
				await mutate(undefined as TVariables)
			})
		} : undefined}
		{...rest}
	>
		{children}
		{isPending ? <x-spinner/> : <></>}
	</button> : <></>
}

class FakeSyntheticEvent<C> implements React.SyntheticEvent<C, undefined> {
	type: string
	nativeEvent: undefined
	currentTarget: EventTarget & C
	target: EventTarget
	timeStamp: number
	bubbles = false
	cancelable = false
	defaultPrevented = false
	eventPhase = 2
	isTrusted = false
	preventDefault(): void {}
	isDefaultPrevented(): boolean { return false }
	#propagationStopped: boolean
	stopPropagation(): void { this.#propagationStopped = true }
	isPropagationStopped(): boolean { return this.#propagationStopped }
	persist(): void {}

	constructor(type: string, currentTarget: EventTarget & C) {
		this.type = type
		this.currentTarget = currentTarget
		this.target = currentTarget
		this.timeStamp = performance.now()
		this.#propagationStopped = false
	}
}

interface MutationSuccessEventInit<TData> {
	data: TData
}

class MutationSuccessEvent<TData, C extends Element> extends FakeSyntheticEvent<C> {
	data: TData

	constructor(type: string, eventInitDict: MutationSuccessEventInit<TData>, currentTarget: C) {
		super(type, currentTarget)
		this.data = eventInitDict.data
	}
}

interface MutationErrorEventInit<TData> {
	error: TData
}

class MutationErrorEvent<TError, C extends Element> extends FakeSyntheticEvent<C> {
	error: TError

	constructor(type: string, eventInitDict: MutationErrorEventInit<TError>, currentTarget: C) {
		super(type, currentTarget)
		this.error = eventInitDict.error
	}
}
